<script setup lang="ts">
import {computed, ref} from "vue";
import {useLocalStorage} from "@vueuse/core";
import {ChevronLeft, ChevronRight, Database} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Skeleton} from "@/core/components/ui/skeleton";
import FrequentationWeekHeatmap from "@/agenda/components/frequentation/FrequentationWeekHeatmap.vue";
import FrequentationYearGrid from "@/agenda/components/frequentation/FrequentationYearGrid.vue";
import FrequentationLegend from "@/agenda/components/frequentation/FrequentationLegend.vue";
import useFrequentation from "@/agenda/composables/useFrequentation";
import useFrequentationYear from "@/agenda/composables/useFrequentationYear";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";

/**
 * The frequentation screen: how the agenda reads the pharmacy's counter load.
 * Two views — the typical week of the week displayed, and the year week by
 * week — so a pharmacist sees why a slot is discouraged before the assistant
 * ever suggests one.
 */
type Mode = 'week' | 'year';

/** Persisted: coming back to the view one left is the habit of a screen. */
const mode = useLocalStorage<Mode>('agenda.frequentation.mode', 'week');

const currentDay = ref(new Date());
const weekStart = computed(() => parseAgendaDate(currentDay.value).startOf('week'));
const weekEnd = computed(() => weekStart.value.endOf('week'));
const isCurrentWeek = computed(() => weekStart.value.isSame(parseAgendaDate(new Date()), 'week'));

const weekLabel = computed(() => {
    const sameMonth = weekStart.value.isSame(weekEnd.value, 'month');

    return `Semaine du ${weekStart.value.format(sameMonth ? 'D' : 'D MMMM')} au ${weekEnd.value.format('D MMMM YYYY')}`;
});

const shiftWeek = (direction: number) => {
    currentDay.value = parseAgendaDate(currentDay.value).add(direction, 'week').toDate();
};

const {frequentation: loadedWeek, isValidating: weekValidating, error: weekError} = useFrequentation(() => weekStart.value);
const {year, isLoading: yearLoading, error: yearError} = useFrequentationYear(() => mode.value === 'year');

/**
 * The loaded week only when it is the one displayed: on navigation the
 * cache hands back the previous week until the new one arrives, and its
 * days would sit under the wrong heading. Until then the view is loading.
 */
const week = computed(() => {
    const loaded = loadedWeek.value;

    return loaded && loaded.week === weekStart.value.format('YYYY-MM-DD') ? loaded : null;
});

const weekLoading = computed(() => week.value === null && (weekValidating.value || loadedWeek.value !== null));

const loading = computed(() => mode.value === 'week' ? weekLoading.value : yearLoading.value);
const failed = computed(() => mode.value === 'week' ? !!weekError.value : !!yearError.value);
const empty = computed(() => mode.value === 'week' ? week.value === null : year.value === null);
</script>

<template>
  <div class="flex h-full flex-col gap-4 p-6">
    <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div class="flex items-center gap-3">
        <router-link :to="{name: 'agenda'}">
          <Button variant="ghost" size="icon" aria-label="Retour à l'agenda">
            <ChevronLeft class="size-4" />
          </Button>
        </router-link>
        <div>
          <h1 class="text-lg font-semibold text-primary">
            Fréquentation
          </h1>
          <p class="flex items-center gap-1.5 text-xs text-gray-500">
            <Database class="size-3.5" />
            Basé sur l'historique des lignes de vente de la pharmacie
          </p>
        </div>
      </div>
      <div class="flex rounded-lg border border-gray-200 bg-white p-0.5">
        <Button
          :variant="mode === 'week' ? 'secondary' : 'ghost'"
          size="sm"
          :aria-pressed="mode === 'week'"
          @click="mode = 'week'"
        >
          Semaine
        </Button>
        <Button
          :variant="mode === 'year' ? 'secondary' : 'ghost'"
          size="sm"
          :aria-pressed="mode === 'year'"
          @click="mode = 'year'"
        >
          Année
        </Button>
      </div>
    </div>

    <div v-if="mode === 'week'" class="flex flex-wrap items-center gap-2">
      <Button variant="outline" :disabled="isCurrentWeek" @click="currentDay = new Date()">
        Cette semaine
      </Button>
      <Button variant="ghost" size="icon" aria-label="Semaine précédente" @click="shiftWeek(-1)">
        <ChevronLeft class="size-4" />
      </Button>
      <Button variant="ghost" size="icon" aria-label="Semaine suivante" @click="shiftWeek(1)">
        <ChevronRight class="size-4" />
      </Button>
      <p class="font-medium">
        {{ weekLabel }}
      </p>
    </div>

    <div class="min-h-0 flex-1 overflow-auto rounded-lg bg-white p-6 shadow">
      <div v-if="loading" class="flex flex-col gap-3">
        <Skeleton v-for="index in 10" :key="index" class="h-9 w-full" />
      </div>
      <div v-else-if="failed" class="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
        <p class="text-sm text-gray-600">
          Impossible de charger la fréquentation. Vérifiez votre connexion, puis réessayez.
        </p>
      </div>
      <p v-else-if="empty" class="p-10 text-center text-sm text-gray-500">
        Pas encore de fréquentation pour cette pharmacie : il faut quelques semaines de
        ventes remontées par le logiciel de caisse pour lire son activité.
      </p>
      <template v-else>
        <FrequentationWeekHeatmap v-if="mode === 'week' && week" :week="week" />
        <FrequentationYearGrid v-else-if="year" :year="year" />
      </template>
    </div>

    <FrequentationLegend :label="mode === 'week' ? 'Fréquentation prévue :' : 'Activité de la semaine :'">
      <span v-if="mode === 'year'" class="flex items-center gap-1.5">
        <span class="size-2.5 rounded-sm border border-dashed border-gray-300" />
        semaine en cours
      </span>
    </FrequentationLegend>
  </div>
</template>
